import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, KeyRound, Mail } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui';
import { AuthShell, authInputCls } from '../components/auth/AuthShell';

/**
 * Forgot-password — email-OTP reset backed by Cognito ForgotPassword /
 * ConfirmForgotPassword. Step 1 emails a one-time code (via SES); step 2 takes
 * the code + a new password. Anti-abuse is enforced by Cognito itself and
 * surfaced here: ForgotPassword is rate-limited (LimitExceededException) so a
 * user cannot spam resets, the code is single-use and expires, and repeated bad
 * codes throttle. We never reveal whether an email exists (a missing user shows
 * the same "code sent" screen).
 *
 * Presentation shares <AuthShell> with the login page, so the split-screen
 * brand panel and logo are identical across both.
 */
export const Route = createFileRoute('/forgot-password')({
  component: ForgotPassword,
});

const POOL_REGION = (import.meta.env.VITE_COGNITO_REGION as string | undefined) ?? 'eu-central-1';
const CLIENT_ID =
  ((import.meta.env.VITE_COGNITO_WEB_CLIENT_ID ??
    import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID) as string | undefined) ?? '';

async function cognitoCall(target: string, body: Record<string, unknown>): Promise<{ __type?: string; message?: string }> {
  const res = await fetch(`https://cognito-idp.${POOL_REGION}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { __type?: string; message?: string };
  if (!res.ok) {
    const e = new Error(data.message || data.__type || `HTTP ${res.status}`);
    (e as Error & { code?: string }).code = (data.__type ?? '').split('#').pop() ?? '';
    throw e;
  }
  return data;
}

function passwordPolicyError(pw: string): string | null {
  if (pw.length < 12) return 'Password must be at least 12 characters.';
  if (!/[A-Z]/.test(pw)) return 'Password must include an uppercase letter.';
  if (!/[a-z]/.test(pw)) return 'Password must include a lowercase letter.';
  if (!/[0-9]/.test(pw)) return 'Password must include a number.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Password must include a symbol.';
  return null;
}

/** Turn Cognito error codes into friendly, security-aware messages. */
function friendlyError(e: unknown, phase: 'request' | 'confirm'): string {
  const code = (e as { code?: string }).code ?? '';
  const msg = (e as Error).message ?? '';
  switch (code) {
    case 'LimitExceededException':
      return 'Too many attempts. For your security, please wait ~15 minutes and try again.';
    case 'TooManyRequestsException':
      return 'Too many requests right now — please slow down and try again shortly.';
    case 'CodeMismatchException':
      return 'That code is incorrect. Check the latest email and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one and try again.';
    case 'InvalidPasswordException':
      return 'That password does not meet the policy (12+ chars, upper, lower, number, symbol).';
    case 'UserNotFoundException':
      // Never reveal existence — treat as success on the request step.
      return phase === 'request' ? '' : 'We could not reset that account. Request a new code.';
    default:
      return msg || 'Something went wrong. Please try again.';
  }
}

function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'confirm' | 'done'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const requestCode = async () => {
    setErr(null);
    if (!email.trim()) return setErr('Enter your email.');
    setLoading(true);
    try {
      await cognitoCall('ForgotPassword', { ClientId: CLIENT_ID, Username: email.trim().toLowerCase() });
      setStep('confirm');
    } catch (e) {
      const c = (e as { code?: string }).code ?? '';
      // Anti-enumeration: a missing user should look identical to success.
      if (c === 'UserNotFoundException') setStep('confirm');
      else setErr(friendlyError(e, 'request'));
    } finally {
      setLoading(false);
    }
  };

  const confirmReset = async () => {
    setErr(null);
    if (!code.trim()) return setErr('Enter the code from your email.');
    const pe = passwordPolicyError(pw);
    if (pe) return setErr(pe);
    if (pw !== pw2) return setErr('Passwords do not match.');
    setLoading(true);
    try {
      await cognitoCall('ConfirmForgotPassword', {
        ClientId: CLIENT_ID,
        Username: email.trim().toLowerCase(),
        ConfirmationCode: code.trim(),
        Password: pw,
      });
      setStep('done');
    } catch (e) {
      setErr(friendlyError(e, 'confirm'));
    } finally {
      setLoading(false);
    }
  };

  const errorBox = err ? (
    <div className="mt-4 flex items-start gap-2 rounded-xl border border-hud-red/40 bg-hud-red/10 px-3 py-2.5">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-hud-red" />
      <span className="text-[12.5px] leading-relaxed text-hud-red">{err}</span>
    </div>
  ) : null;

  const backToSignIn = (
    <button
      type="button"
      onClick={() => navigate({ to: '/login' })}
      className="mt-6 flex items-center gap-1.5 text-[12.5px] text-txt-muted transition-colors hover:text-txt"
    >
      <ArrowLeft size={14} /> Back to sign in
    </button>
  );

  if (step === 'done') {
    return (
      <AuthShell>
        <div className="flex items-center gap-2.5">
          <CheckCircle2 className="size-6 text-neon" />
          <h1 className="font-display text-[26px] font-bold tracking-tight text-txt-bright">Password reset</h1>
        </div>
        <p className="mt-2 text-[14px] leading-relaxed text-txt-2">
          Your password has been updated. You can now sign in with your new password.
        </p>
        <Button className="mt-8" fullWidth onClick={() => navigate({ to: '/login' })} rightIcon={<ArrowRight size={16} />}>
          Back to sign in
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-display text-[26px] font-bold tracking-tight text-txt-bright">Reset your password</h1>
      <p className="mt-2 text-[14px] leading-relaxed text-txt-2">
        {step === 'request'
          ? "Enter your email and we'll send a one-time code to reset your password."
          : `Enter the code we emailed to ${email} and choose a new password.`}
      </p>

      {step === 'request' ? (
        <form
          className="mt-8 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void requestCode();
          }}
        >
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-txt">Email</span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value.trim())}
              placeholder="you@company.com"
              required
              disabled={loading}
              className={authInputCls}
            />
          </label>
          {errorBox}
          <Button type="submit" fullWidth loading={loading} className="mt-1" leftIcon={loading ? undefined : <Mail size={16} />}>
            {loading ? 'Sending…' : 'Send reset code'}
          </Button>
        </form>
      ) : (
        <form
          className="mt-8 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void confirmReset();
          }}
        >
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-txt">Reset code</span>
            <input
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              placeholder="123456"
              required
              disabled={loading}
              className={`${authInputCls} font-mono tracking-[0.3em]`}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-txt">New password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.currentTarget.value)}
              placeholder="At least 12 characters"
              required
              disabled={loading}
              className={authInputCls}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-txt">Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.currentTarget.value)}
              placeholder="Re-enter your new password"
              required
              disabled={loading}
              className={authInputCls}
            />
          </label>
          <p className="text-[12px] leading-relaxed text-txt-muted">
            Use 12+ characters with uppercase, lowercase, a number, and a symbol.
          </p>
          {errorBox}
          <Button type="submit" fullWidth loading={loading} className="mt-1" leftIcon={loading ? undefined : <KeyRound size={16} />}>
            {loading ? 'Resetting…' : 'Reset password'}
          </Button>
          <button
            type="button"
            onClick={() => void requestCode()}
            disabled={loading}
            className="text-[12.5px] text-txt-muted transition-colors hover:text-txt disabled:opacity-50"
          >
            Didn&apos;t get a code? Send another
          </button>
        </form>
      )}

      {backToSignIn}
    </AuthShell>
  );
}
